// 3rd Party
import { FileRejection } from 'react-dropzone';
import React from 'react';

// Components
import UploadSection, { FileUploadProps } from '@/views/components/upload-section/UploadSection';
import { IconButton } from '@/views/components/buttons';
import { FormattedMessageV2 } from '../../i18n';

// Utils
import { formatBytes, formatMessage } from '@/utils/i18n';

// Assets
import SuccessfulUpload from '@/images/design-system-v2/upload/successfulUpload.svg';
import FailedUpload from '@/images/design-system-v2/upload/failedUpload.svg';
import UploadedFile from '@/images/design-system-v2/upload/uploadedFile.svg';

type Props = {
  onDrop: FileUploadProps['onDrop'];
  onRemove: (file: File) => void;
  uploadedFiles: File[];
  fileRejections?: FileRejection[];
  processingError: boolean;
};

const UploadStep: React.FC<Props> = ({ onDrop, onRemove, uploadedFiles, fileRejections, processingError }) => {
  const i18nBasePath = 'Modal.PortfolioAIStatementReader.UploadStep';

  // Config
  const maxSizeInBytes = 5_000_000;
  const maxFiles = 1;
  const accept = {
    'text/csv': ['.csv'],
    'application/pdf': ['.pdf'],
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/png': ['.png'],
  };

  // Status
  const hasFiles = !!uploadedFiles.length;
  const hasRejections = !!fileRejections?.length;
  const maxFilesReached = uploadedFiles.length >= maxFiles && !hasRejections;
  const showFailureStatus = hasRejections || processingError;

  const renderUploadStatus = (success: boolean) => (
    <div className="upload-status">
      <img
        alt={formatMessage(`${i18nBasePath}.Alt.${success ? 'SuccessfulUploadImage' : 'FailedUploadImage'}`)}
        src={success ? SuccessfulUpload : FailedUpload}
      />

      <div className="upload-status-title">
        <FormattedMessageV2 toLocalize={`${i18nBasePath}.${success ? 'SuccessfullyUploaded' : 'FailedUpload'}`} />
      </div>

      <div className="upload-status-subtitle">
        <FormattedMessageV2 toLocalize={`${i18nBasePath}.${success ? 'AllItemsReady' : 'CouldNotCompleteProcessing'}`} />
      </div>
    </div>
  );

  const renderFileDisplay = (file: File, key: string, rejectionCode?: string) => (
    <div className="uploaded-file" key={key}>
      <div className="file-info">
        <img alt={formatMessage(`${i18nBasePath}.Alt.UploadedFileImage`)} src={UploadedFile} />
        <div>
          <div className="uploaded-file-name">{file?.name}</div>
          {!rejectionCode && <div className="uploaded-file-size">{formatBytes(file?.size)}</div>}
          {rejectionCode && (
            <div className="file-rejection-reason">
              <FormattedMessageV2 toLocalize={`${i18nBasePath}.Errors.${rejectionCode}`} />
            </div>
          )}
        </div>
      </div>

      <IconButton
        data-testid="remove-file-icon-button"
        aria-label="Remove File"
        icon="ds-delete-outline"
        className="icon-button-v2 secondary uploaded-file-delete"
        onClick={() => onRemove(file)}
      />
    </div>
  );

  return (
    <div className="ai-statement-reader-upload-step">
      {!showFailureStatus && (
        <>
          {!maxFilesReached && <UploadSection onDrop={onDrop} accept={accept} maxSize={maxSizeInBytes} maxFiles={maxFiles} />}
          {maxFilesReached && renderUploadStatus(true)}
        </>
      )}

      {showFailureStatus && renderUploadStatus(false)}

      <h6 className="uploaded-file-title">
        <FormattedMessageV2 toLocalize={`${i18nBasePath}.UploadedFile`} />
      </h6>

      <div className="uploaded-files">
        {!hasFiles && !hasRejections && (
          <div className="no-file-added">
            <FormattedMessageV2 toLocalize={`${i18nBasePath}.NoFileAdded`} />
          </div>
        )}
        {hasFiles && uploadedFiles.map((file, index) => renderFileDisplay(file, `uploaded-${index}`))}
        {hasRejections &&
          fileRejections?.map((fileRejection, index) => renderFileDisplay(fileRejection.file, `rejection-${index}`, fileRejection.errors[0].code))}
      </div>
    </div>
  );
};

export default UploadStep;
